import React, { FunctionComponent, useContext } from 'react';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';

import * as API from '../../services/API';
import { Scenario, ScenarioArchiveFlag } from '../../types/models';

import APICacheContext from '../shared/APICacheContext';
import APICache from '../../services/APICache';
import PopupDialog from '../shared/PopupDialog';

interface Props {
  scenario: Scenario;
  isOpen: boolean;
  onCancel(): void;
  onComplete(): void;
}

interface State {
  busy: boolean;
  error?: Error;
  name: string;
}

const INITIAL_STATE = {
  busy: false,
  name: '',
  error: undefined,
};

const ArchiveScenarioDialog: FunctionComponent<Props> = ({
  scenario,
  isOpen,
  onCancel,
  onComplete,
}: Props) => {
  const [state, setState] = React.useState<State>(INITIAL_STATE);

  const archiveScenarioDialogContext = useContext(APICacheContext);

  const handleCancel = (): void => {
    onCancel();
    setState(INITIAL_STATE);
  };

  const handleConfirm = async (): Promise<void> => {
    setState({ ...state, busy: true, error: undefined });

    try {
      await API.update(
        `/instances/${scenario.ModelInstanceID}/scenarios/${scenario.id}`,
        { Archive_Flag: ScenarioArchiveFlag.Archive }
      );
    } catch (error: any) {
      setState({ ...state, busy: false, error });
      return;
    }

    await (archiveScenarioDialogContext as APICache).load(
      [
        `/clients`,
        `/instances/${scenario.ModelInstanceID}/scenarios`,
        `/instances/${scenario.ModelInstanceID}/scenarios/${scenario.id}`,
      ],
      true
    );

    onComplete();
    setState(INITIAL_STATE);
  };

  const { busy, error } = state;

  return (
    <PopupDialog
      open={isOpen}
      close={handleCancel}
      title="Archive Scenario"
      isLoading={busy}
      primaryButtons={[
        {
          id: 'archive-scenario',
          label: 'Yes, archive it',
          onClick: handleConfirm,
          disabled: busy,
        },
      ]}
    >
      <Typography>
        Are you sure you want to archive "{scenario.Name}"? You can find it
        later in the "Archive" section.
      </Typography>

      {error && (
        <Box mt={2}>
          <Alert severity="error">
            <AlertTitle>Something went wrong</AlertTitle>
            Unable to archive scenario, check your connection and try again.
          </Alert>
        </Box>
      )}
    </PopupDialog>
  );
};

export default ArchiveScenarioDialog;
